const PAYMENT_SESSION_KEY = "pendingPaymentOrder";

export interface PaymentSession {
  /** Set from payment socket after successful QR payment. */
  orderId?: string;
  /** Display number(s) shown on payment-success (e.g. ORDER #189). */
  orderNumber?: string;
  orderNumbers?: string[];
  /** Temp-data id used for socket registration only. */
  tempDataId?: string;
}

function asNonEmptyString(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined;
  const trimmed = String(value).trim();
  return trimmed.length ? trimmed : undefined;
}

function collectObjects(...sources: unknown[]): Record<string, unknown>[] {
  return sources.filter(
    (source): source is Record<string, unknown> =>
      Boolean(source) && typeof source === "object" && !Array.isArray(source)
  );
}

function pickField(
  objects: Record<string, unknown>[],
  keys: string[]
): string | undefined {
  for (const obj of objects) {
    for (const key of keys) {
      const value = asNonEmptyString(obj[key]);
      if (value) return value;
    }
  }
  return undefined;
}

function readRawSession(): PaymentSession {
  try {
    const raw = localStorage.getItem(PAYMENT_SESSION_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as PaymentSession;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeSession(session: PaymentSession): void {
  try {
    localStorage.setItem(PAYMENT_SESSION_KEY, JSON.stringify(session));
  } catch {
    /* ignore quota errors */
  }
}

function mergeSession(partial: PaymentSession): PaymentSession {
  const merged: PaymentSession = { ...readRawSession() };
  (Object.keys(partial) as Array<keyof PaymentSession>).forEach((key) => {
    const value = partial[key];
    if (value !== undefined) {
      (merged as Record<string, unknown>)[key] = value;
    }
  });
  writeSession(merged);
  return merged;
}

/** Called right after POST /user/payment/temp-data returns (socket registration id). */
export function persistPaymentSessionFromTempData(
  data: Record<string, unknown> | null | undefined
): PaymentSession {
  const objects = collectObjects(data, data?.data as Record<string, unknown> | undefined);
  // New payment attempt — clear previous order display fields.
  const next: PaymentSession = {
    tempDataId: pickField(objects, ["id", "temp_data_id", "tempDataId"]),
  };
  writeSession(next);
  return next;
}

function readSuccessFlag(obj: Record<string, unknown>): boolean {
  const success = obj.success;
  if (success === true || success === "true") return true;

  const status = asNonEmptyString(obj.status)?.toLowerCase();
  return status === "success" || status === "completed" || status === "paid";
}

/** True when a payment socket payload indicates a successful charge. */
export function isPaymentSocketSuccess(data: unknown): boolean {
  const objects = collectObjects(
    data,
    (data as Record<string, unknown> | null | undefined)?.data as Record<string, unknown> | undefined,
    (data as Record<string, unknown> | null | undefined)?.result as Record<string, unknown> | undefined
  );
  return objects.some(readSuccessFlag);
}

function collectOrderNumbers(...sources: unknown[]): string[] {
  const seen = new Set<string>();
  const numbers: string[] = [];

  const pushNumber = (value: unknown) => {
    const next = asNonEmptyString(value);
    if (!next || seen.has(next)) return;
    seen.add(next);
    numbers.push(next);
  };

  const visit = (value: unknown) => {
    if (value == null) return;
    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }
    if (typeof value !== "object") {
      pushNumber(value);
      return;
    }

    const obj = value as Record<string, unknown>;
    pushNumber(obj.orderNumber);
    pushNumber(obj.order_number);
    if (Array.isArray(obj.orderNumbers)) obj.orderNumbers.forEach(pushNumber);
    if (Array.isArray(obj.order_numbers)) obj.order_numbers.forEach(pushNumber);
    if (Array.isArray(obj.orders)) obj.orders.forEach(visit);
    if (obj.order && typeof obj.order === "object") visit(obj.order);
    if (obj.data && typeof obj.data === "object") visit(obj.data);
    if (obj.result && typeof obj.result === "object") visit(obj.result);
  };

  sources.forEach(visit);
  return numbers;
}

/**
 * Called when payment socket reports success.
 * Example payload: { success: true, orderId: "bc1d0432-...", orderNumber: 189 }
 * Multiple: { orders: [{ orderNumber: 189 }, { orderNumber: 190 }] }
 */
/** Persist order ids/numbers returned from POST /product/order/pay-on-reception. */
export function savePendingPaymentOrderFromApiOrders(
  orders: Array<Record<string, unknown>>,
  tempDataId?: string
): PaymentSession {
  const orderNumbers = collectOrderNumbers(orders);
  const first = orders[0] || {};
  const orderId =
    asNonEmptyString(first.id) ||
    asNonEmptyString(first.orderId) ||
    asNonEmptyString(first.order_id);

  return mergeSession({
    tempDataId,
    orderId,
    orderNumber: orderNumbers[0],
    orderNumbers: orderNumbers.length > 0 ? orderNumbers : undefined,
  });
}

export function savePendingPaymentOrderFromSocket(
  data: Record<string, unknown>,
  tempDataId: string
): PaymentSession {
  const nested = collectObjects(
    data,
    data.data as Record<string, unknown> | undefined,
    data.order as Record<string, unknown> | undefined,
    data.result as Record<string, unknown> | undefined
  );

  const orderObject = collectObjects(data.order as Record<string, unknown> | undefined);

  const orderId =
    pickField(nested, ["orderId", "order_id", "product_order_id", "productOrderId"]) ||
    pickField(orderObject, ["orderId", "id", "order_id"]);

  const orderNumbers = collectOrderNumbers(data, data.data, data.result, data.orders, data.order);
  const orderNumber =
    orderNumbers[0] ||
    pickField(nested, ["orderNumber", "order_number"]) ||
    pickField(orderObject, ["orderNumber", "order_number", "number"]);

  const resolvedNumbers =
    orderNumbers.length > 0
      ? orderNumbers
      : orderNumber
        ? [orderNumber]
        : [];

  const merged = mergeSession({
    tempDataId: tempDataId || pickField(nested, ["temp_data_id", "tempDataId"]),
    orderId,
    orderNumber: orderNumber || resolvedNumbers[0],
    orderNumbers: resolvedNumbers.length > 0 ? resolvedNumbers : undefined,
  });

  if (!merged.orderId && resolvedNumbers.length === 0) {
    console.warn("Payment socket success but orderId/orderNumber missing:", merged, "raw:", data);
  } else {
    console.log(
      "Payment order stored from socket success:",
      { orderId: merged.orderId, orderNumber: merged.orderNumber, orderNumbers: merged.orderNumbers },
      "raw:",
      data
    );
  }

  return merged;
}

export function readPaymentSession(): PaymentSession {
  return readRawSession();
}

/** Display order number(s) for payment-success badges. */
export function readPendingOrderNumbers(): string[] {
  const session = readRawSession();
  if (Array.isArray(session.orderNumbers) && session.orderNumbers.length > 0) {
    return session.orderNumbers.map((value) => String(value).trim()).filter(Boolean);
  }
  const single = asNonEmptyString(session.orderNumber);
  return single ? [single] : [];
}

export function readPendingPaymentOrder(): string | null {
  const orderId = readRawSession().orderId;
  return orderId?.trim() ? orderId : null;
}

export function hasPendingPaymentOrder(): boolean {
  return Boolean(readPendingPaymentOrder());
}

export function clearPendingPaymentOrder(): void {
  try {
    localStorage.removeItem(PAYMENT_SESSION_KEY);
  } catch {
    /* ignore */
  }
}
